/**
 * ToolPage Component
 *
 * Unified wrapper for all tool pages following the standard layout pattern:
 * - PageHeader with icon, title, description, and refresh button
 * - Optional content sections (stats, filters, etc.)
 * - Main content area
 *
 * This component eliminates repetitive layout code across tool pages.
 */

import React from "react";
import PropTypes from "prop-types";
import { DashboardLayout } from "../../layouts";
import PageHeader from "./PageHeader";
import { toolThemeVars, resolveControlColor } from "../../utils/toolTheme";
import "./ToolPage.scss";

const ToolPage = ({
  tool,
  // When true, render ONLY the content (and any error). Used when a page is
  // shown as a tab inside another tool — TabbedToolPage already provides the
  // dashboard layout and header, so repeating them would nest two headers.
  embedded = false,
  onRefresh,
  loading = false,
  error = null,
  onRetry,
  children,
  className = "",
  themed = false,
  headerActions = [],
  belowHeaderActions = null,
  onHomeClick,
  homeTooltip,
}) => {
  // Opt-in tool theming: expose the tool's accent palette as CSS custom
  // properties so descendants (incl. shared Button / SegmentedControl, which
  // fall back to the default purple when these aren't set) tint to the tool.
  // ⚠️ CONTROLS vs IDENTITY. This one line tints every themed control in the
  // tool; `iconColor` below, plus the sidebar and dashboard tile, carry the
  // tool's identity. They are the same colour for every tool that has no
  // reason to separate them — but a tool whose CONTENT is colour-coded must
  // separate them, or its own accent competes with the meaning of red and
  // amber. See resolveControlColor.
  const themeStyle = themed
    ? toolThemeVars(resolveControlColor(tool))
    : undefined;

  if (embedded) {
    // Keep the page's own className: each tool page's SCSS is scoped to it
    // (.orders-page, .banner-ads-page …), so rendering a bare fragment left the
    // content completely unstyled.
    return (
      <div className={`tool-page-embedded ${className}`}>
        {error && (
          <div className="tool-page-error">
            <p>Error: {error}</p>
            {onRetry && (
              <button onClick={onRetry} className="retry-btn">
                Try Again
              </button>
            )}
          </div>
        )}
        {children}
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div
        className={`tool-page ${themed ? "tool-page--themed" : ""} ${className}`}
        style={themeStyle}
      >
        <PageHeader
          icon={tool.icon}
          iconColor={tool.color}
          title={tool.name}
          description={tool.description}
          onRefresh={onRefresh}
          loading={loading}
          actions={headerActions}
          showFullscreenToggle={true}
          belowActions={belowHeaderActions}
          onHomeClick={onHomeClick}
          homeTooltip={homeTooltip}
        />

        {error && (
          <div className="tool-page-error">
            <p>Error: {error}</p>
            {onRetry && (
              <button onClick={onRetry} className="retry-btn">
                Try Again
              </button>
            )}
          </div>
        )}

        <div className="tool-page-content">{children}</div>
      </div>
    </DashboardLayout>
  );
};

ToolPage.propTypes = {
  /** Tool object from toolRegistry */
  tool: PropTypes.shape({
    icon: PropTypes.elementType.isRequired,
    color: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
  }).isRequired,

  /** Refresh callback */
  onRefresh: PropTypes.func,

  /** Loading state */
  loading: PropTypes.bool,

  /** Error message */
  error: PropTypes.string,

  /** Retry callback for error state */
  onRetry: PropTypes.func,

  /** Child components (stats, filters, content) */
  children: PropTypes.node.isRequired,
  embedded: PropTypes.bool,

  /** Additional CSS class */
  className: PropTypes.string,

  /** Opt-in: tint shared controls + expose --tool-color* vars from tool.color */
  themed: PropTypes.bool,

  /** Extra buttons in the header action row (passed to PageHeader `actions`) —
      an array of button configs, or a ready-made node (e.g. a SegmentedControl) */
  headerActions: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),

  /** Extra content rendered under the header action row, right-aligned (e.g.
   *  a compact inline notice) — rides in the space the taller title/description
   *  column already occupies, rather than growing the header. */
  belowHeaderActions: PropTypes.node,

  /** When set, the header icon + title become clickable — for a tool "home"
   *  action (e.g. reset back to the tool's default view/sub-view). */
  onHomeClick: PropTypes.func,
  homeTooltip: PropTypes.string,
};

export default ToolPage;
